"use client";

import React, { useState } from "react";
import Image from "next/image";
import EnqueryForm from "@/components/forms/EnqueryForm";
import {
  FinanceAndAccountingSubcategory,
  FormHeading,
  SuccessMessageType,
  VerticalCategory,
} from "@/interface/types";
import CairaScheduleNew from "./CairaScheduleNew";
import {
  useWebinarSchedule,
  useWebinarScheduleLoading,
} from "@/contexts/EventContext";
import { getWebinarEntry, getOrdinalDateParts } from "@/data/webinarSchedule";
import WebinarDateSkeleton from "@/components/common/WebinarDateSkeleton";

const BADGE_IMG =
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/webinar_badge.webp";
const BADGE_IMG_MOB =
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/webinar_badge_mob.webp";
const BANNER_IMG =
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/webinar_banner.webp";
const BANNER_IMG_MOB =
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/webinar_banner_mob.webp";
const SHOWCASE_MOB =
  "https://ik.imagekit.io/mileseducation/miles_website/caira/new/showcase.webp";

const FreeLine = () => {
  const webinarSchedule = useWebinarSchedule();
  const isScheduleLoading = useWebinarScheduleLoading();
  const weeklyWebinarEntry = getWebinarEntry("weekly", webinarSchedule);
  const weeklyDateParts = getOrdinalDateParts(weeklyWebinarEntry.date);

  return (
    <p className="text-white text-xs md:text-[19px] leading-snug ">
      <span className="font-bold">FREE</span>{" "}
      <span className="text-[#F33]">•</span>
      <span className="font-bold text-[#F33]">Live</span>{" "}
      <span className="font-bold">Webinar</span>
      <br className="md:hidden" />{" "}
      {isScheduleLoading ? (
        <WebinarDateSkeleton width={180} />
      ) : (
        <>
          <span className="font-medium">
            {weeklyDateParts.day}
            <sup>{weeklyDateParts.suffix}</sup>{" "}
            {weeklyDateParts.month.slice(0, 3)}, {weeklyWebinarEntry.day} at
          </span>{" "}
          <span className="text-[#FFBA08] font-bold">
            {weeklyWebinarEntry.time}
          </span>
        </>
      )}
    </p>
  );
};

const RegisterButton = ({
  className = "",
  onClick,
}: {
  className?: string;
  onClick: () => void;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center justify-center rounded-[8px] bg-[#39F] hover:bg-[#3b7ce6] transition-colors text-white font-bold text-sm px-5 md:px-10 py-2 md:w-[220px] w-[260px] ${className}`}
  >
    Register for Webinar
  </button>
);

const LiveWebinar = ({ hideForms = false }: { hideForms?: boolean }) => {
  const [isFormOpen, setIsFormOpen] = useState(false);

  const handleRegister = () => {
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
  };

  return (
    <section className="w-full bg-[#111] py-8 md:py-[50px]">
      <div className="main_container mx-auto px-4 md:px-6 md:max-w-[1280px] !pr-0">
        {/* Title */}
        <div className="text-center mb-6 md:mb-8">
          <h2 className="text-[20px] md:text-[30px] font-bold text-[#FFBA08] leading-tight">
            Live CAIRA Webinar
          </h2>
          <p className="text-white text-xs md:text-base font-semibold mt-1">
            A 3-level journey from foundations to leadership
          </p>
        </div>

        {/* ── DESKTOP ── */}
        <div className="hidden md:flex items-center gap-5 lg:gap-8">
          {/* Left — headline + date + CTA */}
          <div className="w-[36%] flex flex-col gap-4">
            <div className="leading-[1.1]">
              <p className="text-white font-bold text-[30px] md:text-[47px]">
                Build your
              </p>
              <p className="font-bold text-[30px] md:text-[47px]">
                <span className="text-[#FFBA08]">1st AI Agent</span>
                <span className="text-white"> in 2 hrs</span>
              </p>
            </div>
            <FreeLine />
            {!hideForms && (
              <RegisterButton className="self-start" onClick={handleRegister} />
            )}
          </div>

          {/* Middle — badge + showcase */}
          <div className="w-[22%] flex flex-col items-center gap-2">
            <Image
              src={BADGE_IMG}
              alt="AI in Accounting 101 — Miles CAIRA"
              width={520}
              height={542}
              className="w-[180px] lg:w-[200px] h-auto object-contain"
            />
            {/* <Image
              src={SHOWCASE_DESK}
              alt="Showcase badge on LinkedIn"
              width={427}
              height={150}
              className="w-[150px] h-auto object-contain"
            /> */}
          </div>

          {/* Right — banner */}
          <div className="flex-1 min-w-0">
            <Image
              src={BANNER_IMG}
              alt="Build AI Agents with Copilot Studio, Microsoft Copilot, Power Automate"
              width={1058}
              height={612}
              className="w-full h-auto object-contain"
            />
          </div>
        </div>

        {/* ── MOBILE ── */}
        <div className="md:hidden">
          <div className="flex gap-2 items-start">
            {/* Left — text + badge + showcase */}
            <div className="w-[46%] shrink-0 flex flex-col text-left gap-3">
              <FreeLine />
              <p className="font-bold text-[22px] leading-[1.15]">
                <span className="text-white">Build your </span>
                <span className="text-[#FFBA08]">1st</span>
                <br />
                <span className="text-[#FFBA08]">AI Agent</span>
                <span className="text-white"> in 2 hrs</span>
              </p>

              <Image
                src={BADGE_IMG_MOB}
                alt="AI in Accounting 101 — Miles CAIRA"
                width={92}
                height={150}
                className="w-[92px] h-auto object-contain mt-1"
              />
              <Image
                src={SHOWCASE_MOB}
                alt="Showcase badge on LinkedIn"
                width={363}
                height={119}
                className="hidden w-[130px] h-auto object-contain"
              />
            </div>

            {/* Right — banner */}
            <div className="flex-1 min-w-0 flex items-center justify-end">
              <Image
                src={BANNER_IMG_MOB}
                alt="Build AI Agents with Copilot Studio, Microsoft Copilot, Power Automate"
                width={279}
                height={335}
                className="w-[279px] h-[335px] max-w-none object-contain"
              />
            </div>
          </div>

          {!hideForms && (
            <div className="flex justify-center">
              <RegisterButton
                className="py-1.5 mt-5"
                onClick={handleRegister}
              />
            </div>
          )}
        </div>

        <CairaScheduleNew />
      </div>

      {!hideForms && isFormOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[999] px-4">
          <div
            className="relative p-6 w-full md:w-[430px] rounded-2xl border"
            style={{
              background: "rgba(33, 100, 167, 0.10)",
              boxShadow: "0 0 54.9px 0 rgba(166, 137, 255, 0.30) inset",
              borderColor: "rgba(255, 255, 255, 0.06)",
              backdropFilter: "blur(8px)",
            }}
          >
            <button
              type="button"
              onClick={closeForm}
              className="absolute right-2 top-0 text-white px-3 py-1 rounded"
            >
              ✕
            </button>
            <EnqueryForm
              course="CAIRA"
              courseId={1}
              formHeading={FormHeading.TALK_TO_CAIRA_ADVISOR}
              formType="page"
              isAStepperForm
              subCategory={FinanceAndAccountingSubcategory.CPA}
              submitButtonName="Enroll Now"
              successMessage={SuccessMessageType.ACCOUNTING}
              verticalName={VerticalCategory.FINANCE_AND_ACCOUNTING}
              darkBackground
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default LiveWebinar;
